/**
 * Resolves the context every fiscal write needs (BP-01/RT-01): the active
 * location, its fiscal device, and the acting staff id. Surfaces what's missing
 * so forms can disable submit with a clear reason instead of failing opaquely.
 */
import { useQuery } from '@tanstack/react-query';

import { useAuth } from './auth.js';
import { useLocations } from './locations.js';
import { supabase } from './supabase.js';

export interface PosContext {
  tenantId: string;
  locationId: string | null;
  fiscalDeviceId: string | null;
  staffId: string | null;
  ready: boolean;
  loading: boolean;
  missing: ('location' | 'device' | 'staff')[];
}

export function usePosContext(): PosContext {
  const { claims } = useAuth();
  const tenantId = claims?.tenant_id ?? '';
  const staffId = claims?.staff_id ?? null;
  const { locations, activeLocationId } = useLocations();
  const locationId = activeLocationId ?? locations[0]?.id ?? null;

  const device = useQuery({
    queryKey: ['fiscal_device', tenantId, locationId],
    enabled: !!tenantId && !!locationId,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<string | null> => {
      const { data, error } = await supabase
        .from('fiscal_devices')
        .select('id')
        .eq('location_id', locationId as string)
        .eq('is_active', true)
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return (data?.id as string | undefined) ?? null;
    },
  });

  const fiscalDeviceId = device.data ?? null;
  const missing: PosContext['missing'] = [];
  if (!locationId) missing.push('location');
  if (!fiscalDeviceId) missing.push('device');
  if (!staffId) missing.push('staff');

  return {
    tenantId,
    locationId,
    fiscalDeviceId,
    staffId,
    ready: missing.length === 0,
    loading: device.isLoading,
    missing,
  };
}
